import { AlertCircle, CheckCircle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const ProblemSolution = () => {
  const { ref, isInView } = useScrollAnimation({ threshold: 0.2, triggerOnce: true });

  const problems = [
    "Your website isn't bringing in new customers",
    "Competitors are outranking you on Google",
    "Using disconnected tools that don't work together",
    "Need a custom app but can't afford enterprise pricing"
  ];

  const solutions = [
    "Get found by customers actively searching for your services",
    "Outrank competitors with proven SEO strategies",
    "Get a custom platform built exactly for your business",
    "All your business tools integrated in one place"
  ];

  return (
    <section ref={ref} className="py-16 md:py-24 bg-background relative overflow-hidden">
      {/* Subtle background effects */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-destructive/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div 
          className={`text-center mb-14 transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Stop Losing Customers to Your Competition
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Most businesses struggle with digital marketing. We make it simple.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 max-w-5xl mx-auto">
          {/* Problems Card */}
          <div 
            className={`group relative bg-gradient-to-br from-destructive/5 via-destructive/8 to-destructive/5 border border-destructive/20 rounded-2xl p-8 hover:border-destructive/40 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-destructive/15 overflow-hidden ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '200ms' }}
          >
            {/* Subtle shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" />
            
            <h3 className="text-xl font-semibold text-foreground mb-6 flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-destructive/10 group-hover:bg-destructive/15 group-hover:scale-110 transition-all duration-300">
                <AlertCircle className="h-6 w-6 text-destructive" />
              </div>
              Sound Familiar?
            </h3>
            <ul className="space-y-4">
              {problems.map((problem, index) => (
                <li 
                  key={index} 
                  className={`flex items-start gap-3 transition-all duration-500 ${isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
                  style={{ transitionDelay: `${300 + index * 100}ms` }}
                >
                  <span className="mt-2 h-2 w-2 rounded-full bg-destructive flex-shrink-0" />
                  <span className="text-muted-foreground">{problem}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions Card */}
          <div 
            className={`group relative bg-gradient-to-br from-accent/5 via-accent/8 to-accent/5 border border-accent/20 rounded-2xl p-8 hover:border-accent/40 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-accent/15 overflow-hidden ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '300ms' }}
          >
            {/* Subtle shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" />
            
            <h3 className="text-xl font-semibold text-foreground mb-6 flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-accent/10 group-hover:bg-accent/15 group-hover:scale-110 transition-all duration-300">
                <CheckCircle className="h-6 w-6 text-accent" />
              </div>
              What We Deliver
            </h3>
            <ul className="space-y-4">
              {solutions.map((solution, index) => (
                <li 
                  key={index} 
                  className={`flex items-start gap-3 transition-all duration-500 ${isInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`}
                  style={{ transitionDelay: `${400 + index * 100}ms` }}
                >
                  <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{solution}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div 
          className={`text-center mt-14 transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ transitionDelay: '800ms' }}
        >
          <Button asChild size="lg" variant="premium" className="group">
            <Link to="/services">
              See How We Can Help
              <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ProblemSolution;
